import * as React from "react";
import { Card } from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { showMoneyInAppFormat } from "../../../../utils/helpers";
import { useFetchInvoices } from "../../hooks/queries/billing.queries";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";

export default function PaymentsOverview() {
    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Hook for fetching all invoice
    const { data, isLoading, error, isSuccess } = useFetchInvoices({
        client_id: clientId,
        limit: LISTS_DEFAULT_LIMIT,
        page: 1,
    });

    const summary = data?.summary;

    const addBracketToAmount = (value: string) => `(${value})`;

    const totalOutstanding =
        (summary?.total_insurance_amount_owed || 0) +
        (summary?.total_coinsurance_amount_owed || 0) -
        (summary?.total_pr_100_amount_owed || 0);

    const totalCollected =
        (summary?.total_insurance_amount_collected || 0) +
        (summary?.total_coinsurance_amount_collected || 0) +
        (summary?.total_unmatched_amount || 0) +
        (summary?.total_pr_100_amount_collected || 0);

    const totalWriteOff =
        (summary?.total_insurance_amount_written_off || 0) +
        (summary?.total_coinsurance_amount_written_off || 0);

    const totalCredits =
        (summary?.total_coinsurance_amount_credited || 0) +
        (summary?.total_pr_100_amount_credited || 0);

    return (
        <>
            {isLoading ? <Loader /> : null}
            {error ? (
                <ListState
                    isError
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display payment info at this time please try again later`
                    }
                    listHeader="Payment Info"
                    stateHelperText="Try reloading this page to solve this issue"
                />
            ) : null}
            {data?.data && isSuccess ? (
                <Card classNames="p-0 rounded-md">
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg-dark] [border-bottom:1px_solid_var(--color-stroke)]  px-[--padding-card-x] py-[--padding-card-y] text-textSec text-caption font-semibold uppercase rounded-tl-md rounded-tr-md">
                        <span />
                        <span> OUTSTANDING</span>
                        <span>COLLECTED</span>
                        <span>WRITE-OFF</span>
                        <span>CREDITS</span>
                    </div>
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg] [border-bottom:1px_solid_var(--color-stroke)]  px-[--padding-card-x] py-[--padding-card-y] text-text text-base font-semibold uppercase">
                        <span className="text-textSec font-medium">
                            INSURANCE
                        </span>
                        <span
                            className={
                                summary?.total_insurance_amount_owed &&
                                summary.total_insurance_amount_owed > 0
                                    ? "text-[--color-text-danger]"
                                    : "text-text"
                            }
                        >
                            {summary?.total_insurance_amount_owed &&
                            summary.total_insurance_amount_owed <= 0
                                ? showMoneyInAppFormat(
                                      Math.abs(
                                          summary.total_insurance_amount_owed
                                      )
                                  )
                                : (summary?.total_insurance_amount_owed &&
                                      addBracketToAmount(
                                          showMoneyInAppFormat(
                                              summary.total_insurance_amount_owed
                                          )
                                      )) ||
                                  showMoneyInAppFormat(0)}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_insurance_amount_collected
                                )) ||
                                0}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_insurance_amount_written_off
                                )) ||
                                0}
                        </span>
                        <span>--</span>
                    </div>
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg-dark] [border-bottom:1px_solid_var(--color-stroke)]  px-[--padding-card-x] py-[--padding-card-y] text-caption font-semibold uppercase">
                        <span className="text-textSec font-medium">
                            CO-INSURANCE
                        </span>
                        <span
                            className={
                                summary?.total_coinsurance_amount_owed &&
                                summary.total_coinsurance_amount_owed > 0
                                    ? "text-[--color-text-danger]"
                                    : "text-text"
                            }
                        >
                            {summary?.total_coinsurance_amount_owed &&
                            summary.total_coinsurance_amount_owed <= 0
                                ? showMoneyInAppFormat(
                                      Math.abs(
                                          summary.total_coinsurance_amount_owed
                                      )
                                  )
                                : (summary?.total_coinsurance_amount_owed &&
                                      addBracketToAmount(
                                          showMoneyInAppFormat(
                                              summary.total_coinsurance_amount_owed
                                          )
                                      )) ||
                                  showMoneyInAppFormat(0)}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_coinsurance_amount_collected
                                )) ||
                                0}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_coinsurance_amount_written_off
                                )) ||
                                0}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_coinsurance_amount_credited
                                )) ||
                                0}
                        </span>
                    </div>
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg] [border-bottom:1px_solid_var(--color-stroke)]  px-[--padding-card-x] py-[--padding-card-y] text-text text-base font-semibold uppercase">
                        <span className="text-textSec font-medium">
                            UNMATCHED
                        </span>
                        <span>--</span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_unmatched_amount
                                )) ||
                                0}
                        </span>
                        <span>--</span>
                        <span>--</span>
                    </div>
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg-dark] [border-bottom:1px_solid_var(--color-stroke)]  px-[--padding-card-x] py-[--padding-card-y] text-text text-caption font-semibold uppercase">
                        <span className="text-textSec text-sm font-medium">
                            PR100
                        </span>
                        <span
                            className={
                                summary?.total_pr_100_amount_owed &&
                                summary.total_pr_100_amount_owed < 0
                                    ? "text-[--color-text-danger]"
                                    : "text-text"
                            }
                        >
                            {summary?.total_pr_100_amount_owed &&
                            summary.total_pr_100_amount_owed < 0
                                ? addBracketToAmount(
                                      showMoneyInAppFormat(
                                          Math.abs(
                                              summary.total_pr_100_amount_owed
                                          )
                                      )
                                  )
                                : (summary?.total_pr_100_amount_owed &&
                                      showMoneyInAppFormat(
                                          summary.total_pr_100_amount_owed
                                      )) ||
                                  showMoneyInAppFormat(0)}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_pr_100_amount_collected
                                )) ||
                                0}
                        </span>
                        <span>--</span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(
                                    summary.total_pr_100_amount_credited
                                )) ||
                                0}
                        </span>
                    </div>
                    <div className="grid grid-cols-5 gap-x-1 bg-[--card-bg]  px-[--padding-card-x] py-[--padding-card-y] text-text text-base font-semibold uppercase rounded-bl-md rounded-br-md">
                        <span className="text-textSec text-sm font-medium">
                            Total
                        </span>
                        <span
                            className={
                                totalOutstanding > 0
                                    ? "text-[--color-text-danger]"
                                    : "text-text"
                            }
                        >
                            {totalOutstanding <= 0
                                ? showMoneyInAppFormat(
                                      Math.abs(totalOutstanding)
                                  )
                                : addBracketToAmount(
                                      showMoneyInAppFormat(totalOutstanding)
                                  )}
                        </span>
                        <span>
                            {(summary &&
                                showMoneyInAppFormat(totalCollected)) ||
                                0}
                        </span>
                        <span>
                            {(summary && showMoneyInAppFormat(totalWriteOff)) ||
                                0}
                        </span>
                        <span>
                            {(summary && showMoneyInAppFormat(totalCredits)) ||
                                0}
                        </span>
                    </div>
                </Card>
            ) : null}
        </>
    );
}
